import http from '@/http/index'
import AppMessage from '@/components/AppMessage/message'

/**
 * 发布页草稿类型。映射后端真实的 draftType 参数。
 */
export type DraftPageType = 'POST' | 'BLIND_BOX'

export interface DraftRecord<T> {
  draftId?: string
  payload: T
  version?: number
  modifiedTime?: string
  schemaVersion?: number
  updatedAt?: number
}

interface DraftGetResponse<T = any> {
  draftId: string | null
  payload: T | null
  version: number | null
  payloadSchemaVersion?: number | null
  modifiedTime: string | null
}

interface DraftSaveResponse {
  draftId: string
  version: number
}

const DRAFT_TYPE_MAP: Record<DraftPageType, string> = {
  POST: 'normal',
  BLIND_BOX: 'blindbox'
}

/**
 * 获取指定发布页的草稿（请求后端 API）。
 */
export async function getDraft<T>(pageType: DraftPageType): Promise<DraftRecord<T> | null> {
  try {
    const draftType = DRAFT_TYPE_MAP[pageType]
    const res = await http.get<DraftGetResponse<T>>('/aigc/api/topic/content/draft', { draftType })
    const data = res?.data
    if (!data || !data.draftId) return null

    return {
      draftId: data.draftId,
      payload: data.payload as T,
      version: data.version ?? undefined,
      modifiedTime: data.modifiedTime ?? undefined,
      schemaVersion: data.payloadSchemaVersion ?? 1,
      updatedAt: data.modifiedTime ? new Date(data.modifiedTime).getTime() : Date.now()
    }
  } catch (error) {
    console.warn('draftApi getDraft failed', pageType, error)
    return null
  }
}

/**
 * 保存发布页草稿（请求后端 API）。
 */
export async function saveDraft<T>(
  pageType: DraftPageType,
  payload: T,
  _schemaVersion = 1
): Promise<{ draftId?: string; version?: number } | undefined> {
  try {
    const draftType = DRAFT_TYPE_MAP[pageType]
    const res = await http.post<DraftSaveResponse>('/aigc/api/topic/content/draft', {
      draftType,
      payload
    })
    const data = res?.data
    return {
      draftId: data?.draftId,
      version: data?.version
    }
  } catch (error: any) {
    console.warn('draftApi saveDraft failed', pageType, error)
    const msg = error?.message || '保存草稿失败，请重试'
    AppMessage({ type: 'error', message: msg })
    return undefined
  }
}

/**
 * 删除发布页草稿（请求后端 API）。
 */
export async function deleteDraft(pageType: DraftPageType, draftId: string): Promise<void> {
  if (!draftId) return
  try {
    await http.post('/aigc/api/topic/content/draft/delete', { draftId: String(draftId) })
  } catch (error: any) {
    console.warn('draftApi deleteDraft failed', pageType, draftId, error)
    const msg = error?.message || '删除草稿失败，请重试'
    AppMessage({ type: 'error', message: msg })
  }
}
